import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import {
  Loader2, Search, RotateCcw, UserX, UserCheck, Trash2, LogIn, X, Crown, Zap, Sparkles, Shield, Pencil, Save, Users,
} from "lucide-react";

const PlanBadge = ({ plan }) => {
  const map = {
    agency: { Icon: Crown, cls: "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10" },
    pro:    { Icon: Zap,   cls: "border-[#FF4400] text-[#FF4400] bg-[#FF4400]/10" },
    free:   { Icon: Sparkles, cls: "border-zinc-700 text-zinc-400 bg-zinc-900" },
  };
  const { Icon, cls } = map[plan] || map.free;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-sm font-mono text-[9px] uppercase tracking-widest font-bold ${cls}`}>
      <Icon className="w-3 h-3" />
      {plan}
    </span>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const { user: me, impersonate } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (includeDeleted) params.include_deleted = true;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.users);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento utenti");
    } finally {
      setLoading(false);
    }
  }, [q, includeDeleted]);

  useEffect(() => { reload(); }, [reload]);

  const onResetCounter = async (u) => {
    if (!window.confirm(`Reset contatore reel di ${u.email}?`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-counter`);
      toast.success(`Reset OK · ${data.reels_backdated} reel spostati`);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const onSoftDelete = async (u) => {
    if (!window.confirm(`Disattivare ${u.email}? L'account non potrà più accedere ma i dati restano.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/soft-delete`);
      toast.success("Utente disattivato");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const onRestore = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/restore`);
      toast.success("Utente riattivato");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const onHardDelete = async (u) => {
    if (!window.confirm(`ATTENZIONE: eliminare DEFINITIVAMENTE ${u.email}?\n\nVerranno cancellati ${u.total_reels} reel, ${u.total_brands} brand, e tutti i video caricati. Operazione IRREVERSIBILE.`)) return;
    if (!window.confirm(`Sei davvero sicuro? Conferma una seconda volta per ${u.email}.`)) return;
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      const d = data.deleted || {};
      toast.success(`Eliminato: ${d.reels || 0} reel, ${d.brands || 0} brand, ${d.videos || 0} video`);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const onImpersonate = async (u) => {
    if (!window.confirm(`Accedere come ${u.email}? Tornerai admin con il bottone "Esci impersonificazione".`)) return;
    try {
      await impersonate(u.id);
      toast.success(`Ora sei loggato come ${u.email}`);
      navigate("/dashboard");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-page">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-8 py-10">
        <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-[#FFB800]" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB800]">Admin</span>
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white">
              Gestione utenti
            </h1>
            <p className="text-zinc-500 font-body mt-2">
              {users.length} utenti {includeDeleted ? "(inclusi disattivati)" : "attivi"}
            </p>
          </div>
        </div>

        {/* Search + filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex-1 min-w-[280px] relative">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per email o nome..."
              data-testid="admin-search"
              className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] rounded-sm font-body"
            />
          </div>
          <label className="inline-flex items-center gap-2 px-4 py-3 border border-zinc-800 rounded-sm cursor-pointer hover:border-zinc-700">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              data-testid="admin-include-deleted"
              className="accent-[#FF4400]"
            />
            <span className="text-sm text-zinc-400 font-body">Mostra disattivati</span>
          </label>
          <button
            onClick={reload}
            data-testid="admin-reload"
            className="inline-flex items-center gap-2 px-4 py-3 border border-zinc-800 hover:border-[#FF4400] hover:text-[#FF4400] text-zinc-400 rounded-sm transition-colors text-sm"
          >
            <RotateCcw className="w-4 h-4" />
            Ricarica
          </button>
        </div>

        {/* Users table */}
        <div className="border border-zinc-800 rounded-sm overflow-hidden bg-zinc-950" data-testid="admin-users-table">
          <div className="hidden md:grid grid-cols-[3fr_1fr_1fr_1fr_2fr] gap-4 px-5 py-3 bg-zinc-900 border-b border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <div>Utente</div>
            <div>Piano</div>
            <div>Reel</div>
            <div>Stato</div>
            <div className="text-right">Azioni</div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-zinc-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Caricamento utenti...
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-16 text-zinc-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              Nessun utente trovato
            </div>
          ) : (
            users.map((u) => (
              <div
                key={u.id}
                className={`md:grid md:grid-cols-[3fr_1fr_1fr_1fr_2fr] flex flex-col gap-2 md:gap-4 md:items-center px-5 py-4 border-b border-zinc-900 last:border-b-0 hover:bg-zinc-900/50 transition-colors ${u.deleted ? "opacity-50" : ""}`}
                data-testid={`admin-user-row-${u.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-medium truncate">{u.name || "(no name)"}</span>
                    {u.is_admin && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] rounded-sm font-mono text-[9px] uppercase tracking-widest">
                        <Shield className="w-2.5 h-2.5" /> admin
                      </span>
                    )}
                    {u.id === me?.id && (
                      <span className="px-1.5 py-0.5 bg-zinc-800 text-zinc-300 rounded-sm font-mono text-[9px] uppercase tracking-widest">tu</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 font-body truncate">{u.email}</div>
                </div>

                <div><PlanBadge plan={u.plan} /></div>

                <div className="text-xs font-mono">
                  <div className="text-white" data-testid={`admin-user-reels-${u.id}`}>
                    <span className="text-[#FF4400] font-bold">{u.reels_this_month}</span>
                    <span className="text-zinc-600"> /mese</span>
                  </div>
                  <div className="text-zinc-500">{u.total_reels} totali</div>
                </div>

                <div>
                  {u.deleted ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/10 border border-red-500/40 text-red-400 rounded-sm font-mono text-[9px] uppercase tracking-widest">
                      <UserX className="w-3 h-3" /> Disattivo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/40 text-emerald-400 rounded-sm font-mono text-[9px] uppercase tracking-widest">
                      <UserCheck className="w-3 h-3" /> Attivo
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-1 justify-start md:justify-end">
                  <IconBtn label="Modifica" Icon={Pencil} onClick={() => setEditingUser(u)} testId={`admin-edit-${u.id}`} />
                  <IconBtn label="Reset counter" Icon={RotateCcw} onClick={() => onResetCounter(u)} testId={`admin-reset-${u.id}`} disabled={u.deleted} />
                  {!u.deleted && u.id !== me?.id && (
                    <IconBtn label="Login come" Icon={LogIn} onClick={() => onImpersonate(u)} testId={`admin-impersonate-${u.id}`} variant="warn" />
                  )}
                  {!u.deleted ? (
                    <IconBtn label="Disattiva" Icon={UserX} onClick={() => onSoftDelete(u)} testId={`admin-softdel-${u.id}`} variant="warn" disabled={u.id === me?.id} />
                  ) : (
                    <IconBtn label="Riattiva" Icon={UserCheck} onClick={() => onRestore(u)} testId={`admin-restore-${u.id}`} />
                  )}
                  <IconBtn label="Elimina definitivamente" Icon={Trash2} onClick={() => onHardDelete(u)} testId={`admin-harddel-${u.id}`} variant="danger" disabled={u.id === me?.id} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); reload(); }}
        />
      )}
    </div>
  );
};

const IconBtn = ({ label, Icon, onClick, testId, variant = "default", disabled = false }) => {
  const cls =
    variant === "danger"
      ? "border-red-500/30 text-red-400 hover:bg-red-500/20 hover:border-red-500"
      : variant === "warn"
      ? "border-[#FFB800]/30 text-[#FFB800] hover:bg-[#FFB800]/15 hover:border-[#FFB800]"
      : "border-zinc-800 text-zinc-400 hover:bg-zinc-800 hover:text-white";
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      data-testid={testId}
      disabled={disabled}
      className={`w-9 h-9 inline-flex items-center justify-center rounded-sm border transition-all disabled:opacity-30 disabled:cursor-not-allowed ${cls}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
};

const EditUserModal = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: user.name || "",
    plan: user.plan || "free",
    is_admin: !!user.is_admin,
    reels_this_month: user.reels_this_month || 0,
    agency_name: user.agency_name || "",
    agency_slug: user.agency_slug || "",
    agency_color_primary: user.agency_color_primary || "",
    agency_tagline: user.agency_tagline || "",
    new_password: "",
  });
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.new_password) delete payload.new_password;
      // Send empty agency strings as null only if user cleared a previously-filled value
      await api.patch(`/admin/users/${user.id}`, payload);
      toast.success(`Utente ${user.email} aggiornato`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-end md:items-center justify-center p-0 md:p-6 overflow-y-auto" data-testid="admin-edit-modal">
      <div className="bg-[#0A0A0C] border border-zinc-800 w-full md:max-w-2xl max-h-[92vh] overflow-y-auto md:rounded-sm">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-zinc-800 sticky top-0 bg-[#0A0A0C] z-10">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400] mb-1">Modifica utente</div>
            <h2 className="font-display font-black text-2xl text-white truncate">{user.email}</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-edit-close"
            className="w-9 h-9 rounded-sm border border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-900 flex items-center justify-center flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <FormRow label="Nome">
            <input
              type="text"
              value={form.name}
              data-testid="admin-edit-name"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white rounded-sm focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] font-body"
            />
          </FormRow>

          <FormRow label="Piano">
            <div className="flex gap-2 flex-wrap">
              {["free", "pro"].map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`admin-edit-plan-${p}`}
                  onClick={() => setForm({ ...form, plan: p })}
                  className={`px-4 py-2 text-sm rounded-sm border font-body ${
                    form.plan === p
                      ? "bg-[#FF4400] border-[#FF4400] text-white font-bold"
                      : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700"
                  }`}
                >
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mt-2">
              · Cambiare piano resetta la scadenza a +30 giorni (free = nessuna scadenza)
            </p>
          </FormRow>

          <FormRow label="Reel questo mese">
            <input
              type="number"
              min="0"
              value={form.reels_this_month}
              data-testid="admin-edit-reels"
              onChange={(e) => setForm({ ...form, reels_this_month: parseInt(e.target.value || "0", 10) })}
              className="w-32 px-4 py-3 bg-zinc-900 border border-zinc-800 text-white rounded-sm focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] font-body"
            />
          </FormRow>

          <FormRow label="Admin">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_admin}
                data-testid="admin-edit-isadmin"
                onChange={(e) => setForm({ ...form, is_admin: e.target.checked })}
                className="accent-[#FFB800] w-4 h-4"
              />
              <span className="text-sm text-zinc-300 font-body">Concedi accesso admin</span>
            </label>
          </FormRow>

          <FormRow label="Nuova password (lascia vuoto per non cambiare)">
            <input
              type="text"
              value={form.new_password}
              data-testid="admin-edit-password"
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              placeholder="Min 6 caratteri"
              className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] font-body"
            />
          </FormRow>

          <div className="border-t border-zinc-800 pt-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4400] mb-3">Workspace agency (solo per piano agency)</div>
            <div className="space-y-4">
              <FormRow label="Nome agency">
                <input
                  type="text"
                  value={form.agency_name}
                  onChange={(e) => setForm({ ...form, agency_name: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white rounded-sm focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] font-body"
                />
              </FormRow>
              <FormRow label="Slug pubblico (lettere/numeri/-)">
                <input
                  type="text"
                  value={form.agency_slug}
                  onChange={(e) => setForm({ ...form, agency_slug: e.target.value })}
                  placeholder="my-studio"
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] font-body font-mono"
                />
              </FormRow>
              <div className="grid grid-cols-2 gap-4">
                <FormRow label="Colore primario">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.agency_color_primary || "#FF4400"}
                      onChange={(e) => setForm({ ...form, agency_color_primary: e.target.value })}
                      className="w-12 h-12 bg-transparent border border-zinc-800 rounded-sm cursor-pointer"
                    />
                    <input
                      type="text"
                      value={form.agency_color_primary}
                      onChange={(e) => setForm({ ...form, agency_color_primary: e.target.value })}
                      placeholder="#FF4400"
                      className="flex-1 px-3 py-3 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] font-mono text-sm"
                    />
                  </div>
                </FormRow>
                <FormRow label="Tagline">
                  <input
                    type="text"
                    value={form.agency_tagline}
                    onChange={(e) => setForm({ ...form, agency_tagline: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 text-white rounded-sm focus:outline-none focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] font-body"
                  />
                </FormRow>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-zinc-800 sticky bottom-0 bg-[#0A0A0C]">
          <button
            onClick={onClose}
            data-testid="admin-edit-cancel"
            className="px-5 py-2.5 text-sm border border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-sm transition-colors"
          >
            Annulla
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="admin-edit-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-50 text-white rounded-sm transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salva modifiche
          </button>
        </div>
      </div>
    </div>
  );
};

const FormRow = ({ label, children }) => (
  <div>
    <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{label}</label>
    {children}
  </div>
);

export default Admin;
